import { useEffect } from 'react';

import { getDocument } from '@atlaskit/browser-apis';

type PopperUpdate = (() => void) | undefined;

/**
 * Repositions an Atlaskit popup when the page layout shifts in a way that moves
 * the popup's trigger without firing a window `resize` or `scroll` event.
 *
 * Popper.js (used by `@atlaskit/popup`) only recomputes position on scroll,
 * window resize, or when its own observed elements resize. Opening a side panel
 * such as Rovo Chat narrows the main content column and shifts the trigger to
 * the left, but none of those signals fire — so the detached popup stays where
 * it was first positioned. This hook watches the document body for size and
 * subtree mutations while the popup is open and calls Popper's `update()` so the
 * popup follows its trigger.
 *
 * @param isOpen  Whether the popup is currently open.
 * @param update  The `update` function provided by the popup `content` render prop.
 */
export const useRepositionOnLayoutShift = (isOpen: boolean, update: PopperUpdate): void => {
	useEffect(() => {
		if (!isOpen || typeof update !== 'function') {
			return;
		}

		const doc = getDocument();

		if (
			!doc?.body ||
			typeof ResizeObserver === 'undefined' ||
			typeof MutationObserver === 'undefined'
		) {
			return;
		}

		let frame = 0;
		const scheduleUpdate = () => {
			if (frame) {
				return;
			}
			frame = requestAnimationFrame(() => {
				frame = 0;
				update();
			});
		};

		// The body resizes when a side panel narrows the main content column.
		const resizeObserver = new ResizeObserver(scheduleUpdate);
		resizeObserver.observe(doc.body);

		// Some layout containers change width without the body's own box changing
		// (e.g. a flex sibling appears). Watch subtree/attribute mutations too.
		const mutationObserver = new MutationObserver(scheduleUpdate);
		mutationObserver.observe(doc.body, {
			attributes: true,
			childList: true,
			subtree: true,
			attributeFilter: ['style', 'class'],
		});

		return () => {
			if (frame) {
				cancelAnimationFrame(frame);
			}
			resizeObserver.disconnect();
			mutationObserver.disconnect();
		};
	}, [isOpen, update]);
};
